import { Feature } from './feature';

export enum RecurlyAddOn {
  TESTCLOUD_SESSION_CROSSBROWSER_FREE = 'testcloud_session_crossbrowser_free',
  TESTCLOUD_SESSION_CROSSBROWSER = 'testcloud_session_crossbrowser',
  TESTCLOUD_SESSION_WEBDESKTOP = 'testcloud_session_webdesktop',
  TESTCLOUD_SESSION_MOBILEAPP = 'testcloud_session_mobileapp',
  TESTCLOUD_SESSION_LIVETESTING = 'testcloud_session_livetesting',
  TESTCLOUD_SESSION_PRIVATEDEVICE = 'testcloud_session_privatedevice',
  // Item-backed add-ons of the True Bundle SKUs (True Manual / True Automation Starter). The key
  // of every member MUST stay identical to the BE `RecurlyAddon` enum NAME: subscription metadata
  // arrives keyed by that name, and `addOns` payloads are keyed by it as well
  // (SingleProductPreviewRequest.addOns: Map<RecurlyAddon, Int>).
  TESTOPSG3_FULLLICENSE = 'testops_g3_fulllicense',
  KSE_PER_USER = 'kse_per-user',
  KRE_FLOATING = 'kre_floating',
  // Seat add-ons of the post-pivot Starters (True Manual = 3x true_platform, True Automation =
  // 3x true_automation). Same rule as above: the KEY is the BE enum NAME - `TRUE_PLATFORM_ONLY`,
  // not `TRUE_PLATFORM` - or the BE rejects the payload with `bundle.addons.mismatch`.
  TRUE_AUTOMATION = 'true_automation',
  TRUE_PLATFORM_ONLY = 'true_platform',
}

/**
 * Add-on CODE -> BE enum NAME (e.g. `testops_g3_fulllicense` -> `TESTOPSG3_FULLLICENSE`).
 *
 * The v2 preview/checkout API takes `addOns` as a map keyed by the BE `RecurlyAddon` enum NAME,
 * which is NOT `code.toUpperCase()` for every member (`kse_per-user` -> `KSE_PER_USER`,
 * `testops_g3_fulllicense` -> `TESTOPSG3_FULLLICENSE`). Deriving the key from the code would make
 * Jackson reject the request, so it is looked up here instead.
 */
export const RECURLY_ADD_ON_NAME_BY_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(RecurlyAddOn).map(([name, code]) => [code, name]),
);

/** BE enum name for an add-on code; the code itself when it is not a known add-on. */
export const getRecurlyAddOnName = (addOnCode: string): string => (
  RECURLY_ADD_ON_NAME_BY_CODE[addOnCode] ?? addOnCode
);

/**
 * Add-on code for a key that may be either the code itself or a BE enum name. Preview requests
 * carry enum names while preview responses carry codes, so the two are joined through this.
 * `toLowerCase()` alone is not enough: `TESTOPSG3_FULLLICENSE` lowercases to
 * `testopsg3_fulllicense`, which matches no line item.
 */
export const toRecurlyAddOnCode = (addOnKey: string): string => (
  (RecurlyAddOn as Record<string, string>)[addOnKey] ?? addOnKey.toLowerCase()
);

export const getFeatureByAddOn = (addOn: string | undefined) => {
  switch (addOn) {
    // True Bundle components. Without these cases the `default` below would label seat / licence
    // quantities as TestCloud "sessions".
    case RecurlyAddOn.TRUE_AUTOMATION:
      return Feature.TRUE_AUTOMATION;
    case RecurlyAddOn.TRUE_PLATFORM_ONLY:
      return Feature.TRUE_PLATFORM_ONLY;
    case RecurlyAddOn.TESTOPSG3_FULLLICENSE:
      return Feature.TESTOPS_G3;
    case RecurlyAddOn.KSE_PER_USER:
      return Feature.PER_USER_KSE;
    case RecurlyAddOn.KRE_FLOATING:
      return Feature.UNLIMITED_ENGINE;
    case RecurlyAddOn.TESTCLOUD_SESSION_WEBDESKTOP:
      return Feature.TESTCLOUD_SESSION_WEB_DESKTOP;
    case RecurlyAddOn.TESTCLOUD_SESSION_MOBILEAPP:
      return Feature.TESTCLOUD_SESSION_MOBILE_APP;
    case RecurlyAddOn.TESTCLOUD_SESSION_LIVETESTING:
      return Feature.TESTCLOUD_SESSION_LIVE_TESTING;
    case RecurlyAddOn.TESTCLOUD_SESSION_PRIVATEDEVICE:
      return Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE;
    case RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER:
    default:
      return Feature.TESTCLOUD_SESSION_CROSS_BROWSER;
  }
};

export const trueSelfServeAddOn = [
  RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER,
  RecurlyAddOn.TESTCLOUD_SESSION_MOBILEAPP,
  RecurlyAddOn.TESTCLOUD_SESSION_PRIVATEDEVICE,
];

export const getAddOnCodeByFeature = (feature: Feature): RecurlyAddOn | undefined => {
  switch (feature) {
    case Feature.TESTCLOUD_SESSION_CROSS_BROWSER:
      return RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER;
    case Feature.TESTCLOUD_SESSION_MOBILE_APP:
      return RecurlyAddOn.TESTCLOUD_SESSION_MOBILEAPP;
    case Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE:
      return RecurlyAddOn.TESTCLOUD_SESSION_PRIVATEDEVICE;
    case Feature.TESTCLOUD_SESSION_LIVE_TESTING:
      return RecurlyAddOn.TESTCLOUD_SESSION_LIVETESTING;
    case Feature.TESTCLOUD_SESSION_WEB_DESKTOP:
      return RecurlyAddOn.TESTCLOUD_SESSION_WEBDESKTOP;
    default:
      return undefined;
  }
};
